import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { preloadAllData, PreloadProgress } from './preload';
import { dataCache } from './DataCache';

interface BootContextValue {
  isBooting: boolean;
  isBooted: boolean;
  bootProgress: PreloadProgress;
  retry: () => void;
  /** 预加载发生过降级（部分请求失败，数据可能不完整） */
  degraded: boolean;
  dismissDegraded: () => void;
}

const BootContext = createContext<BootContextValue>({
  isBooting: false,
  isBooted: false,
  bootProgress: { current: 0, total: 5, label: '' },
  retry: () => { /* no-op */ },
  degraded: false,
  dismissDegraded: () => { /* no-op */ },
});

export function useBoot(): BootContextValue {
  return useContext(BootContext);
}

export { dataCache };

function isAuthenticated(): boolean {
  return sessionStorage.getItem('sw_authenticated') === '1';
}

export function BootProvider({ children }: { children: React.ReactNode }) {
  const [isBooting, setIsBooting] = useState(false);
  const [isBooted, setIsBooted] = useState(false);
  const [bootProgress, setBootProgress] = useState<PreloadProgress>({
    current: 0, total: 5, label: '准备同步...',
  });
  const [bootError, setBootError] = useState<string | null>(null);
  const [degraded, setDegraded] = useState(false);
  const [dismissedDegraded, setDismissedDegraded] = useState(false);
  // 重试计数：retry() 通过自增该值强制预加载 effect 重新执行
  const [retryNonce, setRetryNonce] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated()) return;
    if (isBooted || isBooting) return;

    // 尝试从 sessionStorage 恢复快照（刷新时跳过网络请求，毫秒级启动）
    if (dataCache.restoreSnapshot()) {
      setIsBooted(true);
      return;
    }

    setIsBooting(true);
    setBootError(null);
    preloadAllData(setBootProgress)
      .then((degradedFlag) => {
        if (degradedFlag) {
          // 降级：不写快照，避免空数据快照长期掩盖真实数据
          setDegraded(true);
        } else {
          dataCache.saveSnapshot();
        }
        setIsBooted(true);
      })
      .catch((err: any) => setBootError(err.message || '数据同步失败'))
      .finally(() => setIsBooting(false));
  }, [location.pathname, isBooted, isBooting, retryNonce]);

  // 刷新/关闭页面前把最新缓存快照写回 sessionStorage，
  // 避免刷新后恢复的是旧快照（发布状态、章节正文等保存结果不丢失）
  useEffect(() => {
    if (!isAuthenticated()) return;
    const flush = () => dataCache.saveSnapshot();
    window.addEventListener('beforeunload', flush);
    window.addEventListener('pagehide', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      window.removeEventListener('pagehide', flush);
    };
  }, []);

  const retry = useCallback(() => {
    dataCache.invalidateAll();
    setDegraded(false);
    setDismissedDegraded(false);
    setIsBooted(false);
    setRetryNonce((n) => n + 1);
  }, []);

  const dismissDegraded = useCallback(() => setDismissedDegraded(true), []);

  if (!isAuthenticated()) {
    return <>{children}</>;
  }

  if (!isBooted) {
    return <PreloadScreen progress={bootProgress} error={bootError} onRetry={retry} />;
  }

  return (
    <BootContext.Provider value={{ isBooting, isBooted, bootProgress, retry, degraded, dismissDegraded }}>
      {children}
      {degraded && !dismissedDegraded && (
        <div style={{
          position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
          background: 'var(--card-bg, #fff)', color: 'var(--text-color, #333)',
          border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, maxWidth: 'calc(100vw - 32px)',
        }}>
          <span role="img" aria-label="warn" style={{ fontSize: 16 }}>⚠️</span>
          <span>部分数据加载失败，当前显示可能不完整</span>
          <button
            onClick={retry}
            style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
              background: 'var(--primary-color, #6366f1)', color: '#fff',
              border: 'none', borderRadius: 6,
            }}
          >
            重试
          </button>
          <button
            onClick={dismissDegraded}
            style={{
              padding: '4px 8px', fontSize: 12, cursor: 'pointer',
              background: 'transparent', color: 'var(--text-secondary, #666)',
              border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6,
            }}
          >
            忽略
          </button>
        </div>
      )}
    </BootContext.Provider>
  );
}

function PreloadScreen({
  progress,
  error,
  onRetry,
}: {
  progress: PreloadProgress;
  error: string | null;
  onRetry: () => void;
}) {
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-color)',
      flexDirection: 'column',
    }}>
      <div style={{
        background: 'var(--card-bg)',
        borderRadius: 12,
        padding: '48px 40px',
        boxShadow: '0 4px 24px var(--shadow-color, rgba(0,0,0,0.08))',
        width: '100%',
        maxWidth: 400,
        textAlign: 'center',
      }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-color)', margin: '0 0 8px' }}>小天书</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 32px' }}>个人写作工具</p>

        {error ? (
          <div style={{ color: 'var(--text-secondary)', lineHeight: 1.8 }}>
            <p style={{ fontSize: 48, margin: '0 0 16px' }}>&#9888;</p>
            <p style={{ fontWeight: 600, color: 'var(--text-color)', margin: '0 0 8px' }}>数据同步失败</p>
            <p style={{ margin: '0 0 16px', color: 'var(--danger-color)' }}>{error}</p>
            <button
              onClick={onRetry}
              style={{
                width: '100%', padding: '10px 0', fontSize: 14, fontWeight: 600,
                backgroundColor: 'var(--primary-color)', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer',
              }}
            >
              重试
            </button>
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 24 }}>
              <svg width="48" height="48" viewBox="0 0 48 48" style={{ animation: 'spin 1s linear infinite', color: 'var(--primary-color)' }}>
                <circle cx="24" cy="24" r="20" fill="none" stroke="currentColor" strokeWidth="3" strokeDasharray="94.2" strokeDashoffset="31.4" strokeLinecap="round" />
              </svg>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-color)', margin: '0 0 8px', fontWeight: 600 }}>
              {progress.label}
            </p>
            <div style={{
              width: '100%', height: 4, backgroundColor: 'var(--border-color)',
              borderRadius: 2, overflow: 'hidden', marginBottom: 8,
            }}>
              <div style={{
                width: `${pct}%`, height: '100%',
                backgroundColor: 'var(--primary-color)',
                borderRadius: 2, transition: 'width 0.3s ease',
              }} />
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', margin: 0 }}>
              {progress.current} / {progress.total}
            </p>
          </div>
        )}

        <div style={{ marginTop: 32 }}>
          <a href="https://beian.miit.gov.cn" target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--text-secondary)', textDecoration: 'none' }}>
            沪ICP备2026031193号-1
          </a>
        </div>
      </div>
    </div>
  );
}
